import { toTitleCase } from '../../../../../../utils/utils';
import { Citation, ChipInfo, EntityRationale, HoverPayload } from './types';
import { PAYMENT_METHOD_NOISE } from './constants';

/** Returns true when the rationale string is a payment-method token with no entity signal. */
export function isNoiseRationale(rationale: string): boolean {
    return PAYMENT_METHOD_NOISE.has(rationale.toLowerCase().trim());
}

const SOURCE_CHIP: Record<string, { label: string; hover: string }> = {
    gst_sales: { label: 'GST', hover: 'Matched to GST sales data' },
    gst_purchase: { label: 'GST', hover: 'Matched to GST purchase data' },
    known_lender: { label: 'KNOWN LENDER', hover: 'Matched to known NBFC/lender list' },
    debt_schedule_emi: { label: 'DEBT SCHEDULE', hover: 'Matched to debt schedule EMI' },
    debt_schedule_sanction: { label: 'DEBT SCHEDULE', hover: 'Matched to debt schedule sanction' },
    bank_loan: { label: 'BANK LOAN', hover: 'Matched to bank list (loan credit)' },
    related_party: { label: 'RELATED PARTY', hover: 'Matched to related party list' },
    director: { label: 'DIRECTOR', hover: 'Matched to director list' },
};

/**
 * Derive a chip from a structured source field (tier-1 rows only).
 * Returns null when source is null/undefined or not a recognised value.
 */
export function getSourceChipInfo(source: string | null | undefined): ChipInfo | null {
    if (!source) return null;
    const chip = SOURCE_CHIP[source];
    if (!chip) return null;
    return {
        label: chip.label,
        isLender: false,
        hoverPayload: { entityRationale: chip.hover, primaryDocName: null, allCitations: [] },
        entityId: -1,
    };
}

/** Keyed by exact rationale string from the API. Entries with null/empty rationale are skipped. */
export function buildRationaleMap(entities: EntityRationale[]): Map<string, EntityRationale> {
    const map = new Map<string, EntityRationale>();
    for (const entity of entities) {
        if (!entity.rationale) continue;
        map.set(entity.rationale, entity);
    }
    return map;
}

/**
 * Pick the primary citation to display on the chip.
 * Priority: gst source first; else document source sorted by doc_name asc;
 * else first citation by doc_name asc regardless of source.
 */
export function pickPrimaryCitation(citations: Citation[]): Citation | null {
    if (!citations.length) return null;

    const gst = citations.find((c) => c.source === 'gst');
    if (gst) return gst;

    const docs = citations.filter((c) => c.source === 'document').sort((a, b) => (a.doc_name ?? '').localeCompare(b.doc_name ?? ''));
    if (docs.length) return docs[0];

    return [...citations].sort((a, b) => (a.doc_name ?? '').localeCompare(b.doc_name ?? ''))[0];
}

/** Label shown on the provenance chip. */
export function chipLabel(primary: Citation): string {
    if (primary.source === 'gst') return 'GST';
    if (primary.doc_type) return primary.doc_type;
    if (primary.source) return toTitleCase(primary.source);
    // Citation row carries no source/doc_type — the identification is ask-aica reasoning only.
    return 'ASK-AICA';
}

/** Full hover payload for the tooltip. */
export function hoverPayload(entity: EntityRationale): HoverPayload {
    const primary = pickPrimaryCitation(entity.citations);
    return {
        entityRationale: entity.entityRationale,
        primaryDocName: primary?.doc_name ?? null,
        allCitations: entity.citations.map((c) => ({ doc_type: c.doc_type, doc_name: c.doc_name })),
    };
}

/**
 * Derive chip info for a single entity. Returns null when the entity has no
 * usable label (no citations and no entityRationale fallback).
 */
export function chipInfoForEntity(entity: EntityRationale): ChipInfo | null {
    const primary = pickPrimaryCitation(entity.citations);
    // No citations: a populated entityRationale means the identification came from an
    // ask-aica call — label it as such. No rationale and no citations -> no chip.
    const label = primary ? chipLabel(primary) : entity.entityRationale ? 'ASK-AICA' : null;
    if (!label) return null;

    return {
        label,
        isLender: entity.type === 'lender_nbfc',
        hoverPayload: hoverPayload(entity),
        entityId: entity.entityId,
    };
}

/**
 * Derive chip info for a subrow. Returns null when the rationale string
 * is not in the map (junction-less row — classification text still renders).
 */
export function getChipInfo(rationaleString: string, rationaleMap: Map<string, EntityRationale>): ChipInfo | null {
    const entity = rationaleMap.get(rationaleString);
    if (!entity) return null;
    return chipInfoForEntity(entity);
}
